/**
 * School billing-account data layer — `/api/billing-school/account/*`.
 *
 * Feeds the /orders/school/checkout insufficient-funds gate (balance +
 * billing mode + credit limit + early-pay rate for the ORGANIZATION
 * account the submit will debit) and the school-scoped top-up flow the
 * reused `TopUpSheet` fires (Stripe intent + local test credit).
 *
 * The top-up fetchers are plain functions (no useMutation wrappers):
 * the checkout binds them with its `schoolLocationId` and hands them to
 * `TopUpSheet`'s `api` prop, which wraps them in its own mutations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  BillingLedgerEntry,
  BillingPaymentMethod,
  CreateTopUpIntentVariables,
  StripeIntentResult,
  TestTopUpResult,
} from './billing-family'
import { apiFetch } from './http'

/** Wire shape of `GET /api/billing-school/account` (lean by design). */
export interface SchoolBillingAccount {
  accountId: string
  balanceCents: number
  currency: string
  /** NULL = no explicit Prepaid/Invoiced selection yet; the submit 402s (`billing_mode_unset`). */
  billingMode: 'PREPAID' | 'INVOICED' | null
  /** INVOICED only; null = unlimited credit (the ledger never refuses). */
  creditLimitCents: number | null
  /** Raw negotiated rate; applies only while the account is PREPAID. */
  earlyPaymentDiscountBps: number | null
  /**
   * Saved cards on the ORGANIZATION account (org-shared by design) — the
   * checkout's top-up sheet shows the default with an "Organization
   * payment method" label.
   */
  paymentMethods: BillingPaymentMethod[]
  defaultPaymentMethodRef: string | null
}

/**
 * Query key — one entry per campus so the checkout gate can target its
 * own school for refetch/polling after a top-up.
 */
export const schoolBillingAccountQueryKey = (schoolLocationId: string) =>
  ['billing-school', 'account', schoolLocationId] as const

export async function fetchSchoolBillingAccount(
  schoolLocationId: string,
  signal?: AbortSignal,
): Promise<SchoolBillingAccount> {
  return apiFetch<SchoolBillingAccount>('/api/billing-school/account', {
    query: { schoolLocationId },
    signal,
  })
}

export function useSchoolBillingAccountQuery(schoolLocationId: string | null) {
  return useQuery<SchoolBillingAccount>({
    queryKey: schoolBillingAccountQueryKey(schoolLocationId ?? 'none'),
    queryFn: ({ signal }) => fetchSchoolBillingAccount(schoolLocationId as string, signal),
    enabled: Boolean(schoolLocationId),
    staleTime: 30_000,
  })
}

/** Wire shape of `GET /api/billing-school/account/ledger` (the Balance view's history). */
export interface SchoolBillingLedgerPayload {
  accountId: string
  currency: string
  /** Same projection the family route nests under `ledger.edges[].node`, newest first. */
  entries: BillingLedgerEntry[]
}

export const schoolBillingLedgerQueryKey = (schoolLocationId: string) =>
  ['billing-school', 'account-ledger', schoolLocationId] as const

export async function fetchSchoolBillingLedger(
  schoolLocationId: string,
  signal?: AbortSignal,
): Promise<SchoolBillingLedgerPayload> {
  return apiFetch<SchoolBillingLedgerPayload>('/api/billing-school/account/ledger', {
    query: { schoolLocationId },
    signal,
  })
}

export function useSchoolBillingLedgerQuery(schoolLocationId: string | null) {
  return useQuery<SchoolBillingLedgerPayload>({
    queryKey: schoolBillingLedgerQueryKey(schoolLocationId ?? 'none'),
    queryFn: ({ signal }) => fetchSchoolBillingLedger(schoolLocationId as string, signal),
    enabled: Boolean(schoolLocationId),
    staleTime: 30_000,
  })
}

export interface SchoolTopUpVariables extends CreateTopUpIntentVariables {
  schoolLocationId: string
}

/** Create a Stripe PaymentIntent that tops up the school's billing account. */
export async function createSchoolTopUpIntent(vars: SchoolTopUpVariables): Promise<StripeIntentResult> {
  return apiFetch<StripeIntentResult>('/api/billing-school/account/top-up-intent', {
    method: 'POST',
    body: vars,
  })
}

/** Local-dev only (Stripe unconfigured): credits the school account directly. */
export async function createSchoolTestTopUp(vars: SchoolTopUpVariables): Promise<TestTopUpResult> {
  return apiFetch<TestTopUpResult>('/api/billing-school/account/top-up/test', {
    method: 'POST',
    body: vars,
  })
}

// ---------------------------------------------------------------------------
// Payment methods on the ORGANIZATION account
// ---------------------------------------------------------------------------
//
// The org counterpart of `/api/billing/payment-methods/*`. Two differences from
// the family hooks, both from the org account having no guardians:
//
//   - every call carries `schoolLocationId` (the campus the SCHOOL_ADMIN gate
//     checks; the account itself is org-level), so these are hooks bound to a
//     campus rather than the family surface's free functions;
//   - there is no card-sharing endpoint. Org cards are usable by every
//     SCHOOL_ADMIN on the org by design, so there is nobody to share with.

export interface SchoolPaymentMethodVariables {
  schoolLocationId: string
  /** The Stripe `pm_…` id (the mirror row id also resolves server-side). */
  paymentMethodId: string
}

export interface CreateSchoolTestPaymentMethodVariables {
  schoolLocationId: string
  brand: string
  last4: string
  expMonth: number
  expYear: number
}

/** Start a Stripe SetupIntent that saves a card against the org account. */
export async function createSchoolSetupIntent(schoolLocationId: string): Promise<StripeIntentResult> {
  return apiFetch<StripeIntentResult>('/api/billing-school/account/setup-intent', {
    method: 'POST',
    body: { schoolLocationId },
  })
}

/** Local-dev only (Stripe unconfigured): saves a fake card on the org account. */
export async function createSchoolTestPaymentMethod(
  vars: CreateSchoolTestPaymentMethodVariables,
): Promise<BillingPaymentMethod> {
  return apiFetch<BillingPaymentMethod>('/api/billing-school/account/payment-methods/test', {
    method: 'POST',
    body: vars,
  })
}

export async function setSchoolDefaultPaymentMethod(vars: SchoolPaymentMethodVariables): Promise<BillingPaymentMethod> {
  return apiFetch<BillingPaymentMethod>(
    `/api/billing-school/account/payment-methods/${encodeURIComponent(vars.paymentMethodId)}/default`,
    { method: 'PATCH', body: { schoolLocationId: vars.schoolLocationId } },
  )
}

export async function removeSchoolPaymentMethod(vars: SchoolPaymentMethodVariables): Promise<void> {
  await apiFetch<void>(`/api/billing-school/account/payment-methods/${encodeURIComponent(vars.paymentMethodId)}`, {
    method: 'DELETE',
    query: { schoolLocationId: vars.schoolLocationId },
  })
}

export function useSchoolSetupIntentMutation() {
  return useMutation({ mutationFn: createSchoolSetupIntent })
}

/**
 * Every write below invalidates the campus's account read — that query owns
 * `paymentMethods` + `defaultPaymentMethodRef`, so it is the one thing that has
 * to refetch for the card list to reflect the change.
 */
function useSchoolAccountMutation<TVars, TResult>(
  schoolLocationId: string,
  mutationFn: (vars: TVars) => Promise<TResult>,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schoolBillingAccountQueryKey(schoolLocationId) })
    },
  })
}

export function useCreateSchoolTestPaymentMethodMutation(schoolLocationId: string) {
  return useSchoolAccountMutation(schoolLocationId, createSchoolTestPaymentMethod)
}

export function useSetSchoolDefaultPaymentMethodMutation(schoolLocationId: string) {
  return useSchoolAccountMutation(schoolLocationId, setSchoolDefaultPaymentMethod)
}

export function useRemoveSchoolPaymentMethodMutation(schoolLocationId: string) {
  return useSchoolAccountMutation(schoolLocationId, removeSchoolPaymentMethod)
}
